// Tab completion for the Terminal command line (#4398, epic #4391). Pure: a
// line in, the replaceable word's start offset and its candidates out. The
// CommandLine component owns cycling (repeated Tab steps through the list)
// and the one rule that keeps completion from becoming a keyboard trap — it
// only swallows Tab when this returns at least one candidate.
//
// Two vocabularies, both reused rather than copied: the first word completes
// over the parser's own verb table (`COMMAND_VERBS`), and a later word after
// `filter` or `tail` completes over the filter grammar's own keys
// (`TERMINAL_FILTER_KEYS`), offered with their `=` so the next keystroke is
// the value. A word already carrying `=` is a value, and nothing completes it.

import { COMMAND_VERBS } from './commands';
import { TERMINAL_FILTER_KEYS } from './filter';

export type Completion = {
  /** Offset in the line where the word being completed starts. */
  readonly start: number;
  /** Replacements for `line.slice(start)`, in cycle order. Empty = nothing to complete. */
  readonly candidates: readonly string[];
};

const NONE = (start: number): Completion => ({ start, candidates: [] });

/** Verbs whose later words are `key=value` filter terms. */
const FILTER_VERBS: ReadonlySet<string> = new Set(['filter', 'tail']);

/**
 * Completion candidates for the LAST word of `line` (the caret is assumed at
 * the end — the only place Tab completion is offered). An empty word never
 * completes: Tab on an empty prompt, or after a trailing space, moves focus as
 * it always does.
 */
export function completeCommandLine(line: string): Completion {
  const start = line.search(/\S+$/);
  if (start === -1) return NONE(line.length);
  const word = line.slice(start);
  const before = line.slice(0, start).trim();
  if (before === '') {
    return { start, candidates: COMMAND_VERBS.filter((v) => v.startsWith(word)) };
  }
  const verb = before.split(/\s+/)[0] ?? '';
  if (!FILTER_VERBS.has(verb) || word.includes('=')) return NONE(start);
  return {
    start,
    candidates: TERMINAL_FILTER_KEYS.filter((k) => k.startsWith(word)).map((k) => `${k}=`),
  };
}
